import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import {
  fetchLatestBidAllotmentEventsByReferenceNumber,
  fetchLatestBidGroupEmpEventsByReferenceNumber,
  fetchLatestBidLineEventsByReferenceNumber,
} from "../../api/bid.js";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system.js";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { getS3ObjectName, longPollingBidFetch } from "../../util/bid/bidCommon.js";
import { BID_ACTIONS, BID_STATUS, BID_TYPE } from "../../util/bid/bidConstants.js";
import { buildBidS3Data, buildTimeOffBidS3Data } from "../../util/bid/bidEventBuilder";
import { UNKNOWN_ACTION_MESSAGE } from "../../util/messageConstants.js";
import ConfirmButton from "../fundamentals/ConfirmButton.jsx";

const BidAdminActionConfirm = (props) => {
  const userSession = useSelector(selectUserSession);

  const getActionConfirmButtonBasedOnActionSelected = (action) => {
    switch (action) {
      case BID_ACTIONS.DELETE:
        return getActionConfirmButton(BID_ACTIONS.DELETE);
      case BID_ACTIONS.WITHDRAW:
        return getActionConfirmButton(BID_ACTIONS.WITHDRAW);
      default:
        return UNKNOWN_ACTION_MESSAGE(action);
    }
  };

  const getActionConfirmButton = (action) => {
    return (
      <ConfirmButton
        variant="contained"
        color="primary"
        onClick={() => handleActionClicked(action)}
        isLoading={props.isActionLoading}
      >
        {action}
      </ConfirmButton>
    );
  };

  const getBidDataWithUpdatedStatus = (action) => {
    let bidData = { ...props.bid };

    bidData.status = getUpdatedStatus(action);
    bidData.scheduledStatus = getUpdatedScheduledStatus(action);

    return bidData;
  };

  const getUpdatedStatus = (action) => {
    if (action === BID_ACTIONS.DELETE) {
      return BID_STATUS.DELETED;
    }

    if (action === BID_ACTIONS.WITHDRAW) {
      return BID_STATUS.WITHDRAWN;
    }

    throw Error("Unknown Action");
  };

  const getUpdatedScheduledStatus = (action) => {
    if (action === BID_ACTIONS.DELETE || action === BID_ACTIONS.WITHDRAW) {
      return "";
    }
    throw Error("Unknown Action");
  };

  const fetchAndBuildTimeOffBidS3Data = async (bidData, s3ObjectName) => {
    let bidAllotments, bidGroup;
    await fetchLatestBidAllotmentEventsByReferenceNumber(userSession.eid, bidData.referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          bidAllotments = response.events;
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Allotment Error: %j`, err);
      });
    await fetchLatestBidGroupEmpEventsByReferenceNumber(userSession.eid, bidData.referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          bidGroup = response.events;
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Group Error: %j`, err);
      });
    return buildTimeOffBidS3Data(userSession, bidData, bidAllotments, bidGroup, s3ObjectName);
  };

  const fetchAndBuildShiftBidS3Data = async (bidData, s3ObjectName) => {
    let bidLines, bidGroup;
    await Promise.all([
      fetchLatestBidLineEventsByReferenceNumber(userSession.eid, bidData.referenceNumber)
        .then((res) => res.json())
        .then((response) => {
          if (response.events) {
            bidLines = response.events;
          } else {
            throw Error();
          }
        }),
      fetchLatestBidGroupEmpEventsByReferenceNumber(userSession.eid, bidData.referenceNumber)
        .then((res) => res.json())
        .then((response) => {
          if (response.events) {
            bidGroup = response.events;
          } else {
            throw Error();
          }
        }),
    ]);

    return buildBidS3Data(userSession, bidData, bidLines, bidGroup, s3ObjectName);
  };

  const onActionSuccess = () => {
    props.onClickDialogClose();
    if (props.onSetIsActionSuccess) {
      props.onSetIsActionSuccess(true);
    }
  };

  const onActionError = () => {
    props.onSetActionErrors("Unexpected Exception occurred.");
    props.onSetIsActionLoading(false);
    props.onSetIsActionError(true);
  };

  const handleActionClicked = async (action) => {
    if (!props.isFormValidationSuccess()) {
      return;
    }

    props.onSetIsActionLoading(true);

    let bidData = getBidDataWithUpdatedStatus(action);

    if (containsReasonComments()) {
      bidData.comments = props.reasonComments;
    }

    if (containsLaborContact()) {
      bidData.laborContact = props.laborContact;
    }

    let bidS3Data = {};
    const s3ObjectName = await getS3ObjectName(bidData.pk, bidData.station);
    if (
      bidData.bidType === BID_TYPE.FREEDAY ||
      bidData.bidType === BID_TYPE.FTO ||
      bidData.bidType === BID_TYPE.TWU_DAT_FTO ||
      bidData.bidType === BID_TYPE.IAM_DAT ||
      bidData.bidType === BID_TYPE.NC_PTO_FTO ||
      bidData.bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      bidS3Data = await fetchAndBuildTimeOffBidS3Data(bidData, s3ObjectName);
    }

    if (bidData.bidType === BID_TYPE.SHIFT) {
      bidS3Data = await fetchAndBuildShiftBidS3Data(bidData, s3ObjectName);
    }

    try {
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();
      if (result.presignedUrl) {
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          await longPollingBidFetch(bidData.referenceNumber, userSession, requestId);
          onActionSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error in handleActionClicked: ", err);
      onActionError();
    }
  };

  const containsReasonComments = () => {
    return props.reasonComments !== "";
  };

  const containsLaborContact = () => {
    return props.laborContact !== "";
  };

  return getActionConfirmButtonBasedOnActionSelected(props.actionSelected);
};

BidAdminActionConfirm.propTypes = {
  bid: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onSetIsActionError: PropTypes.func.isRequired,
  onSetActionErrors: PropTypes.func.isRequired,
  onSetIsActionLoading: PropTypes.func.isRequired,
  onSetIsActionSuccess: PropTypes.func.isRequired,
  isActionLoading: PropTypes.bool.isRequired,
  reasonComments: PropTypes.string.isRequired,
  isFormValidationSuccess: PropTypes.func.isRequired,
  laborContact: PropTypes.string,
};

export default BidAdminActionConfirm;
